'use client'

// LiveGaugeHero — the right-hand hero column (giant CFS number +
// condition badge + trend + gauge-height + section). It swaps ALL of
// those values when the user picks a non-primary gauge from the
// GaugeSwitcher popover, so the hero reflects the gauge the user
// actually selected instead of staying stuck on the primary.
//
// Why this exists / how it stays hydration-safe:
//   The river page is `dynamic = 'force-static'`. The hero is
//   server-prerendered with the PRIMARY gauge so the page stays
//   CDN-cacheable. This component renders those same primary values
//   on first paint (initialized straight from `default*` props, no
//   Date math, no useSearchParams) so the client render matches the
//   server HTML byte-for-byte — no React #418, no hydration warning.
//   Only AFTER a client-only signal fires (the GAUGE_CHANGE_EVENT the
//   GaugeSwitcher dispatches, a `popstate`, or a direct `?gauge=<id>`
//   link read post-mount) does it fetch the alternate gauge and swap.
//
// It also absorbs the old LiveFlowBadge's job: a 60s poll of the
// primary gauge's /api/gauge/[id]/flow so a transient USGS outage at
// build time doesn't leave the hero showing "—" for the full
// revalidate window. (See LiveFlowBadge's header comment for the
// original rationale.)

import { useEffect, useState } from 'react'
import Link from 'next/link'
import type { FlowCondition } from '@/types'
import { formatCfs } from '@/lib/usgs'
import StaffGauge from '@/components/rivers/StaffGauge'
import { GAUGE_CHANGE_EVENT } from '@/components/rivers/AlternateGaugeOverlay'

// Just the hero-relevant slice of FlowData. Scalars only — serializable
// across the RSC boundary and all we render here.
export interface HeroFlow {
  cfs: number | null
  gaugeHeightFt: number | null
  condition: FlowCondition
  rateLabel: string
  changePerHour: number | null
  changeIn3Hours: number | null
}

interface Props {
  riverId: string
  primaryGaugeId: string
  // optRange like "175–450" — used to draw the StaffGauge band and
  // passed to the API so it can classify the alternate gauge.
  optRange: string
  // true = dark hero (RIVER_V2 default): white 52px CFS. false = light
  // hero: navy 42px CFS. Matches the old LiveFlowBadge `dark` prop.
  dark: boolean
  // Server-rendered primary-gauge values. First paint renders these.
  defaultFlow: HeroFlow
  defaultSection: string | null
}

const COND_LABEL: Record<string, string> = {
  optimal:  'Optimal',
  low:      'Below Optimal',
  high:     'Above Optimal',
  flood:    'Flood',
  loading:  'Loading…',
  no_gauge: 'No realtime gauge',
  no_data:  'No current reading',
}

const COND_CLASS: Record<string, string> = {
  optimal:  'cond-opt',
  low:      'cond-low',
  high:     'cond-high',
  flood:    'cond-flood',
  loading:  'cond-loading',
  no_gauge: 'cond-loading',
  no_data:  'cond-loading',
}

const POLL_INTERVAL_MS = 60_000

export default function LiveGaugeHero({
  riverId, primaryGaugeId, optRange, dark, defaultFlow, defaultSection,
}: Props) {
  // Which gauge the user has requested. null = primary (server data).
  // Read from the URL + GAUGE_CHANGE_EVENT exactly like
  // AlternateGaugeOverlay (useSearchParams() is forced empty on a
  // force-static route, so we can't use it).
  const [requested, setRequested] = useState<string | null>(null)

  // Primary-gauge values, refreshed by the 60s poll. Seeded from the
  // server-rendered defaults so first paint is unchanged.
  const [primaryFlow, setPrimaryFlow] = useState<HeroFlow>(defaultFlow)

  // Alternate-gauge values, populated after a fetch. null until loaded.
  const [altFlow, setAltFlow] = useState<HeroFlow | null>(null)
  const [altSection, setAltSection] = useState<string | null>(null)
  const [altLoading, setAltLoading] = useState(false)

  const isAlternate =
    !!requested && requested !== primaryGaugeId && /^[A-Za-z0-9]+$/.test(requested)

  // ── signal wiring: URL + popstate + GaugeSwitcher's custom event ──
  useEffect(() => {
    const readUrl = () => {
      try { setRequested(new URLSearchParams(window.location.search).get('gauge')) }
      catch { setRequested(null) }
    }
    const onGaugeChange = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail && typeof detail.gaugeId === 'string') setRequested(detail.gaugeId)
      else readUrl()
    }
    readUrl()
    window.addEventListener('popstate', readUrl)
    window.addEventListener(GAUGE_CHANGE_EVENT, onGaugeChange)
    return () => {
      window.removeEventListener('popstate', readUrl)
      window.removeEventListener(GAUGE_CHANGE_EVENT, onGaugeChange)
    }
  }, [])

  // ── fetch the alternate gauge whenever the request changes ──
  useEffect(() => {
    if (!isAlternate) {
      setAltFlow(null)
      setAltSection(null)
      setAltLoading(false)
      return
    }
    let cancel = false
    setAltLoading(true)
    fetch(
      `/api/gauge/${encodeURIComponent(requested!)}/flow` +
      `?riverId=${encodeURIComponent(riverId)}` +
      `&optRange=${encodeURIComponent(optRange)}` +
      `&primaryGaugeId=${encodeURIComponent(primaryGaugeId)}`,
      { cache: 'no-store' },
    )
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const d = await r.json()
        if (cancel) return
        const f = d.flow as HeroFlow
        setAltFlow({
          cfs: f.cfs,
          gaugeHeightFt: f.gaugeHeightFt,
          condition: f.condition,
          rateLabel: f.rateLabel,
          changePerHour: f.changePerHour,
          changeIn3Hours: f.changeIn3Hours,
        })
        setAltSection(d.meta?.river_section ?? null)
        setAltLoading(false)
      })
      .catch(() => {
        if (cancel) return
        // Fall back to the primary gauge on error — the hero keeps
        // showing valid (if primary) data rather than a broken state.
        setAltFlow(null)
        setAltSection(null)
        setAltLoading(false)
      })
    return () => { cancel = true }
  }, [requested, isAlternate, riverId, optRange, primaryGaugeId])

  // ── 60s primary-gauge refresh (absorbed from LiveFlowBadge) ──
  useEffect(() => {
    if (!primaryGaugeId || !riverId) return
    let cancelled = false
    const inFlight = { current: false }
    const poll = async () => {
      if (inFlight.current) return
      inFlight.current = true
      try {
        const res = await fetch(
          `/api/gauge/${encodeURIComponent(primaryGaugeId)}/flow?riverId=${encodeURIComponent(riverId)}`,
          { cache: 'no-store' },
        )
        if (!res.ok) return
        const data = await res.json()
        if (cancelled || !data?.flow) return
        setPrimaryFlow((prev) => ({
          ...prev,
          cfs: typeof data.flow.cfs === 'number' ? data.flow.cfs : prev.cfs,
          gaugeHeightFt: typeof data.flow.gaugeHeightFt === 'number'
            ? data.flow.gaugeHeightFt : prev.gaugeHeightFt,
        }))
      } catch {
        // Network blip — keep existing values.
      } finally {
        inFlight.current = false
      }
    }
    const t = setInterval(poll, POLL_INTERVAL_MS)
    return () => { cancelled = true; clearInterval(t) }
  }, [riverId, primaryGaugeId])

  // ── pick what to display ──
  // While an alternate is requested but its fetch hasn't landed yet we
  // keep showing the primary values (no flicker), matching the old
  // overlay's behavior of leaving the hero on the primary gauge.
  const showingAlt = isAlternate && !!altFlow
  const flow = showingAlt ? altFlow! : primaryFlow
  const section = showingAlt ? altSection : defaultSection

  // StaffGauge band bounds — parse "175–450" once (same logic the
  // server hero used).
  const parts = (optRange ?? '').split(/[–—-]/).map((s) => parseFloat(s.trim()))
  const optMin = parts.length === 2 && isFinite(parts[0]) ? parts[0] : null
  const optMax = parts.length === 2 && isFinite(parts[1]) ? parts[1] : null

  const condClass = COND_CLASS[flow.condition] ?? 'cond-loading'

  return (
    <>
      {/* When the river has no gauge assigned (flow.condition is
          'no_gauge', set in fetchGaugeData when gaugeId is empty) or an
          assigned gauge is offline/not reporting ('no_data'), we hide
          the pulsing live-data dot. The dot implies live streaming
          flow — keeping it on a stale "—" when there's no live reading
          is misleading. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', marginBottom: '4px' }}>
        {flow.condition !== 'no_gauge' && flow.condition !== 'no_data' && (
          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--rv)', flexShrink: 0 }} className="pulse-dot" />
        )}
        <StaffGauge
          currentCfs={flow.cfs}
          optMin={optMin}
          optMax={optMax}
          condition={flow.condition}
        />
        {/* Giant CFS number. The inline-block min-width reserves a
            fixed box so swapping "262" → "19.4" doesn't shift the
            StaffGauge to its left (CLS-safe). */}
        <span
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: dark ? '52px' : '42px',
            fontWeight: 700,
            color: dark ? '#fff' : '#0C447C',
            lineHeight: 1,
            display: 'inline-block',
            textAlign: 'right',
            minWidth: '4ch',
            opacity: altLoading ? 0.55 : 1,
            transition: 'opacity .15s ease',
          }}
        >
          {formatCfs(flow.cfs)}
        </span>
        <span
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: '14px',
            color: dark ? 'rgba(255,255,255,0.6)' : 'var(--wt)',
          }}
        >
          CFS
        </span>
        {flow.gaugeHeightFt !== null && (
          <div
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: dark ? '13px' : '12px',
              color: dark ? 'rgba(255,255,255,0.65)' : 'var(--tx2)',
              marginTop: '4px',
              fontWeight: dark ? 500 : 400,
            }}
          >
            {flow.gaugeHeightFt.toFixed(2)} ft gauge height
          </div>
        )}
      </div>

      {/* Section label — shown when a non-primary gauge is selected, so
          the user always knows which section the CFS represents. */}
      {section && (
        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', color: 'var(--tx3)', marginBottom: '4px', letterSpacing: '.3px' }}>
          {section}
        </div>
      )}

      <span style={{
        fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px',
        padding: '4px 14px', borderRadius: '20px', fontWeight: 600,
        display: 'inline-block', marginBottom: '8px',
      }} className={condClass}>
        {COND_LABEL[flow.condition]}
      </span>

      {/* When no gauge is assigned, surface an immediate path to fix
          it. The /contact form is the catch-all reporting channel. */}
      {flow.condition === 'no_gauge' && (
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px',
          color: dark ? 'rgba(255,255,255,0.55)' : 'var(--tx3)',
          lineHeight: 1.5, marginBottom: '8px',
        }}>
          Know of a USGS gauge for this river?{' '}
          <Link href="/contact" style={{ color: dark ? 'rgba(255,255,255,0.85)' : 'var(--rvdk)', textDecoration: 'underline' }}>
            Tell us →
          </Link>
        </div>
      )}

      {/* Rate of change / trend */}
      {flow.cfs !== null && (() => {
        const rate = flow.changePerHour
        const isStable = flow.rateLabel === 'Stable'
        const isUnknown = flow.rateLabel === 'Rate unknown' || rate === null

        let color: string
        let arrow = ''
        if (isUnknown) {
          color = 'var(--tx3)'
        } else if (isStable) {
          color = 'var(--tx3)'
          arrow = '→'
        } else {
          const absRate = Math.abs(rate ?? 0)
          const isRising = (rate ?? 0) > 0
          arrow = isRising ? '↑' : '↓'
          if (isRising) {
            if (absRate > 300) color = '#A32D2D'
            else if (absRate > 100) color = '#BA7517'
            else color = '#3CA86E'
          } else {
            if (absRate > 300) color = '#6E4BB4'
            else if (absRate > 100) color = '#0C447C'
            else color = '#5B8DBF'
          }
        }

        return (
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px',
            color, fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
            gap: '6px',
          }}>
            {arrow && <span style={{ fontSize: '13px' }}>{arrow}</span>}
            <span>{flow.rateLabel}</span>
            {flow.changeIn3Hours !== null && Math.abs(flow.changeIn3Hours) >= 25 && (
              <span style={{ fontWeight: 400, color: 'var(--tx3)' }}>
                ({flow.changeIn3Hours > 0 ? '+' : ''}{flow.changeIn3Hours.toLocaleString()} in 3h)
              </span>
            )}
          </div>
        )
      })()}
    </>
  )
}
